import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "utils/cn";
import { useState } from "react";
import Eye from "assets/images/SVG/eye-solid.svg";
import EyeOff from "assets/images/SVG/eye-slash.svg";
import { CustomSelect } from "components/atoms";

// Define input size variants
const inputVariants = cva(
  `
    flex w-full bg-background border border-input shadow-sm shadow-black/5 transition-[color,box-shadow] text-foreground placeholder:text-muted-foreground/80 
    focus-visible:ring-ring/30  focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px]     
    disabled:cursor-not-allowed disabled:opacity-60 
    [&[readonly]]:bg-muted/80 [&[readonly]]:cursor-not-allowed
    file:h-full [&[type=file]]:py-0 file:border-solid file:border-input file:bg-transparent 
    file:font-medium file:not-italic file:text-foreground file:p-0 file:border-0 file:border-e
    aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20
  `,
  {
    variants: {
      variant: {
        lg: "h-10 px-4 text-sm rounded-md file:pe-4 file:me-4",
        md: "h-8 px-3 text-[0.8125rem] leading-[--text-sm--line-height] rounded-md file:pe-3 file:me-3",
        sm: "h-7 px-2.5 text-xs rounded-md file:pe-2.5 file:me-2.5",
      },
    },
    defaultVariants: {
      variant: "lg",
    },
  },
);

const inputAddonVariants = cva(
  "flex items-center shrink-0 justify-center bg-muted border border-input shadow-sm shadow-[rgba(0,0,0,0.05)] text-secondary-foreground [&_svg]:text-secondary-foreground/60",
  {
    variants: {
      variant: {
        sm: "rounded-md h-7 min-w-7 text-xs px-2.5 [&_svg:not([class*=size-])]:size-3.5",
        md: "rounded-md h-8.5 min-w-8.5 px-3 text-[0.8125rem] leading-(--text-sm--line-height) [&_svg:not([class*=size-])]:size-4.5",
        lg: "rounded-md h-10 min-w-10 px-4 text-sm [&_svg:not([class*=size-])]:size-4.5",
      },
      mode: {
        default: "",
        icon: "px-0 justify-center",
      },
    },
    defaultVariants: {
      variant: "md",
      mode: "default",
    },
  },
);

const inputGroupVariants = cva(
  `
    flex items-stretch
    [&_[data-slot=input]]:grow
    [&_[data-slot=input-addon]:has(+[data-slot=input])]:rounded-e-none [&_[data-slot=input-addon]:has(+[data-slot=input])]:border-e-0
    [&_[data-slot=input-addon]:has(+[data-slot=datefield])]:rounded-e-none [&_[data-slot=input-addon]:has(+[data-slot=datefield])]:border-e-0 
    [&_[data-slot=input]+[data-slot=input-addon]]:rounded-s-none [&_[data-slot=input]+[data-slot=input-addon]]:border-s-0
    [&_[data-slot=input-addon]:has(+[data-slot=button])]:rounded-e-none
    [&_[data-slot=input]+[data-slot=button]]:rounded-s-none
    [&_[data-slot=button]+[data-slot=input]]:rounded-s-none
    [&_[data-slot=input-addon]+[data-slot=input]]:rounded-s-none
    [&_[data-slot=input-addon]+[data-slot=datefield]]:[&_[data-slot=input]]:rounded-s-none
    [&_[data-slot=datefield]:has(+[data-slot=input-addon])]:[&_[data-slot=input]]:rounded-e-none
    [&_[data-slot=input]:has(+[data-slot=button])]:rounded-e-none
    [&_[data-slot=input]:has(+[data-slot=input-addon])]:rounded-e-none
    [&_[data-slot=datefield]]:grow
    [&_[data-slot=datefield]+[data-slot=input-addon]]:rounded-s-none [&_[data-slot=datefield]+[data-slot=input-addon]]:border-s-0
  `,
  {
    variants: {},
    defaultVariants: {},
  },
);

const inputWrapperVariants = cva(
  `
    flex items-center gap-1.5
    has-[:focus-visible]:ring-ring/30 
    has-[:focus-visible]:border-ring
    has-[:focus-visible]:outline-none 
    has-[:focus-visible]:ring-[3px]

    [&_[data-slot=datefield]]:grow 
    [&_[data-slot=input]]:data-focus-within:ring-transparent  
    [&_[data-slot=input]]:data-focus-within:ring-0 
    [&_[data-slot=input]]:data-focus-within:border-0 
    [&_[data-slot=input]]:flex 
    [&_[data-slot=input]]:w-full 
    [&_[data-slot=input]]:outline-none 
    [&_[data-slot=input]]:transition-colors 
    [&_[data-slot=input]]:text-foreground
    [&_[data-slot=input]]:placeholder:text-muted-foreground 
    [&_[data-slot=input]]:border-0 
    [&_[data-slot=input]]:bg-transparent 
    [&_[data-slot=input]]:p-0
    [&_[data-slot=input]]:shadow-none 
    [&_[data-slot=input]]:focus-visible:ring-0 
    [&_[data-slot=input]]:h-auto 
    [&_[data-slot=input]]:disabled:cursor-not-allowed
    [&_[data-slot=input]]:disabled:opacity-50    

    [&_svg]:text-muted-foreground 
  `,
  {
    variants: {
      variant: {
        sm: "gap-1.25 [&_svg:not([class*=size-])]:size-3.5",
        md: "gap-1.5 [&_svg:not([class*=size-])]:size-4",
        lg: "gap-1.5 [&_svg:not([class*=size-])]:size-4",
      },
    },
    defaultVariants: {
      variant: "md",
    },
  },
);

function Input({
  className,
  isRequired,
  label,
  isLowercase,
  labelClassName,
  inputClassName,
  type,
  name,
  value,
  isSpace,
  disabled = false,
  maxLength = 1000,
  onChange,
  variant,
  error,
  errorMsg,
  isNumeric,
  isNumericSign,
  isTextOnly,
  ...props
}) {
  const handleKeyDown = (e) => {
    const key = e.key;
    const value = e.currentTarget.value;

    const allowedKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
    ];
    if (allowedKeys.includes(key)) return;

    if (isNumeric) {
      // Only allow digits 0–9
      if (!/^\d$/.test(key)) {
        e.preventDefault();
      }

      // Optional: prevent leading space or leading zeros
      if (key === " " || (key === "0" && value.length === 0)) {
        e.preventDefault();
      }
    }

    if (isTextOnly) {
      // Allow letters
      if (/^[a-zA-Z]$/.test(key)) return;

      // Allow space only if not first character
      if (key === " " && value.length > 0) return;

      // Otherwise block
      e.preventDefault();
    }
  };

  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (isLowercase) {
      inputValue = inputValue.toLowerCase();
    }
    if (isNumericSign) {
      // Allow only: numbers, x/X, *, ", ', ., and spaces
      inputValue = inputValue.replace(/[^0-9xX*"' .]/g, "");
    }

    if (isSpace) {
      inputValue = inputValue.replace(/^\s+/, "");
    }
    if (maxLength) {
      inputValue = inputValue.slice(0, maxLength);
    }
    if (isNumeric) {
      inputValue = inputValue.replace(/[^0-9]/g, "");
      // strip leading zero if you want to match your keydown logic
      if (inputValue.length > 1) {
        inputValue = inputValue.replace(/^0+(?=\d)/, "");
      }
    }
    onChange({
      target: {
        name,
        value: inputValue,
      },
    });
  };

  const handlePaste = (e) => {
    e.preventDefault(); // stop the default browser paste

    const pastedText = e.clipboardData.getData("text");
    let newValue = e.currentTarget.value + pastedText;

    if (isNumeric) {
      // Block anything other than digits
      if (/[^0-9]/.test(pastedText)) {
        e.preventDefault();
      }

      // Block paste if starts with space
      if (/^\s/.test(pastedText)) {
        e.preventDefault();
      }
    }

    if (isTextOnly) {
      // Allow Arabic + English + spaces
      if (/[^a-zA-Z\s]/.test(pastedText)) {
        e.preventDefault();
      }

      // Prevent leading space
      if (/^\s/.test(pastedText) && value.length === 0) {
        e.preventDefault();
      }
    }

    if (isSpace) {
      newValue = newValue.replace(/^\s+/, ""); // remove leading spaces
    }

    if (maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    // call onChange manually
    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  return (
    <div className={cn("py-2", className)}>
      {typeof label === "string" ? (
        <>
          <label
            htmlFor={props?.name}
            className={cn("font-medium text-[14px] mb-1", labelClassName)}
          >
            {label} {isRequired && <span className="text-[#FF5C5C]">*</span>}
          </label>
          <br />
        </>
      ) : (
        label
      )}
      <div className="relative">
        <input
          data-slot="input"
          type={type}
          value={value}
          name={name}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={cn(
            inputVariants({ variant }),
            `${inputClassName} ${error ? "border-red-500" : ""}`,
          )}
          {...props}
        />
        {error ? (
          <div className="text-[0.8125rem] text-red-500">{errorMsg}</div>
        ) : null}
      </div>
    </div>
  );
}

function InputAddon({ className, variant, mode, ...props }) {
  return (
    <div
      data-slot="input-addon"
      className={cn(inputAddonVariants({ variant, mode }), className)}
      {...props}
    />
  );
}

function InputGroup({ className, ...props }) {
  return (
    <div
      data-slot="input-group"
      className={cn(inputGroupVariants(), className)}
      {...props}
    />
  );
}

function InputWrapper({ className, variant, ...props }) {
  return (
    <div
      data-slot="input-wrapper"
      className={cn(
        inputVariants({ variant }),
        inputWrapperVariants({ variant }),
        className,
      )}
      {...props}
    />
  );
}

function PasswordInput({
  className,
  variant,
  inputClassName,
  labelClassName,
  error,
  name,
  value,
  isSpace,
  onChange,
  errorMsg,
  ...props
}) {
  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (isSpace) {
      inputValue = inputValue.replace(/^\s+/, "");
    }

    onChange({
      target: {
        name,
        value: inputValue,
      },
    });
  };
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  return (
    <div className={cn(className)}>
      {props?.label && (
        <label
          htmlFor={props?.name}
          className={cn(
            "inline-block mb-1 text-base font-medium ",
            labelClassName,
          )}
        >
          {props?.label}{" "}
          {props?.isRequired && <span className="text-[#FF5C5C]">*</span>}
        </label>
      )}
      <div className="relative ">
        <input
          data-slot="input"
          type={showPassword ? "text" : "password"}
          value={value}
          name={name}
          onChange={handleInputChange}
          className={cn(
            inputVariants({ variant }),
            `${inputClassName} ${error ? "border-red-500" : ""} pr-10`,
          )}
          {...props}
        />

        <button
          type="button"
          className="absolute right-[2px] top-[11px] px-[10px]  "
          onClick={handleTogglePasswordVisibility}
        >
          {showPassword ? (
            <img src={Eye} alt="eye" width={"20px"} height={"20px"} />
          ) : (
            <img src={EyeOff} alt="eye" width={"20px"} height={"20px"} />
          )}
        </button>
      </div>
      {error ? (
        <div className="text-[0.8125rem] text-red-500 ">{errorMsg}</div>
      ) : null}
    </div>
  );
}

function PhoneInput({
  className,
  inputClassName,
  labelClassName,
  name,
  label,
  isRequired,
  variant,
  error,
  errorMsg,
  value = "",
  countryCode,
  onCountryChange,
  onChange,
  options,
  disabled,
  isSpace,
  maxLength = 15,
  ...props
}) {
  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (isSpace) {
      inputValue = inputValue.replace(/^\s+/, "");
    }

    if (maxLength) {
      inputValue = inputValue.slice(0, maxLength);
    }

    onChange({
      target: {
        name,
        value: `${inputValue}`,
        phone: inputValue,
      },
    });
  };

  const handleKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
    ];
    if (allowedKeys.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (/[^0-9]/.test(pasted)) {
      e.preventDefault();
    }
  };

  return (
    <div className={cn("py-2", className)}>
      {typeof label === "string" && (
        <>
          <label
            htmlFor={name}
            className={cn(
              "font-semibold text-[14px] text-foreground",
              labelClassName,
            )}
          >
            {label}
            {isRequired && <span className="text-[#FF5C5C]">*</span>}
          </label>
          <br />
        </>
      )}

      <div className="relative w-full flex items-center gap-2">
        {/* Select wrapper (relative) */}
        <div className="relative w-[25%]">
          <CustomSelect
            val={countryCode}
            disabled={disabled}
            onChange={onCountryChange}
            options={options}
            className={"w-full m-0"}
            placeholder={""}
          />

          {/* Dropdown Icon */}
        </div>

        {/* Input field for phone number */}
        <input
          type="tel"
          name={name}
          value={value}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={cn(
            inputVariants({ variant }),
            "w-full rounded-md", // input takes full remaining space
            inputClassName,
            error ? "border-red-500" : "",
          )}
          {...props}
        />
      </div>

      {error && (
        <div className="text-[0.8125rem] text-red-500 mt-1">{errorMsg}</div>
      )}
    </div>
  );
}

export {
  Input,
  InputAddon,
  InputGroup,
  InputWrapper,
  inputVariants,
  inputAddonVariants,
  PasswordInput,
  PhoneInput,
};
